import { useEffect, useMemo, useState } from 'react';
import { Plus, Save, Trash2, Search, AlertCircle } from 'lucide-react';
import type { RecordModel } from 'pocketbase';
import { pb, errorText } from '../../lib/pb';
import { useCollection } from '../../lib/hooks';
import { confirmDialog } from '../ConfirmDialog';

/**
 * Generic CRUD for influencers, products and locations.
 *
 * All three are flat records that differ only in their fields, so one editor
 * driven by a field spec beats three near-identical screens that drift apart —
 * which is exactly what happened to n8n's four copies of the image prompt.
 */

export type FieldType = 'text' | 'textarea' | 'url' | 'number' | 'bool' | 'select' | 'relation';

export interface FieldSpec {
  name: string;
  label: string;
  type: FieldType;
  help?: string;
  options?: string[];
  /** For `relation` fields. */
  collection?: string;
  multiple?: boolean;
  rows?: number;
  required?: boolean;
}

export const INFLUENCER_FIELDS: FieldSpec[] = [
  { name: 'influencer_id', label: 'ID', type: 'text', required: true, help: 'Stable key, e.g. DR_YANG' },
  { name: 'name', label: 'Name', type: 'text', required: true },
  { name: 'active', label: 'Active', type: 'bool' },
  {
    name: 'gender',
    label: 'Gender',
    type: 'select',
    options: ['male', 'female', 'neutral'],
    help: 'Used for pronouns in prompts. v1 hardcoded "she" for everyone.',
  },
  { name: 'base_image_url', label: 'Base image (Drive link)', type: 'url', required: true },
  { name: 'avatar_url', label: 'Avatar (Drive link)', type: 'url' },
  { name: 'character_lock', label: 'Character lock', type: 'textarea', rows: 4 },
  { name: 'setting_lock', label: 'Setting lock', type: 'textarea', rows: 3 },
  { name: 'prompt', label: 'Script-writer prompt', type: 'textarea', rows: 10 },
  { name: 'guidelines', label: 'Script formula / guidelines', type: 'textarea', rows: 10 },
  { name: 'custom_prompt', label: 'Custom-script segmenter prompt', type: 'textarea', rows: 8 },
  {
    name: 'allowed_users',
    label: 'Restricted to',
    type: 'relation',
    collection: 'users',
    multiple: true,
    help: 'Leave empty to make this influencer available to everyone.',
  },
];

export const PRODUCT_FIELDS: FieldSpec[] = [
  { name: 'product_id', label: 'ID', type: 'text', required: true },
  { name: 'brand', label: 'Brand', type: 'text' },
  { name: 'name', label: 'Name', type: 'text', required: true },
  { name: 'active', label: 'Active', type: 'bool' },
  { name: 'link', label: 'Product page', type: 'url' },
  { name: 'image_url', label: 'Product image (Drive link)', type: 'url' },
  { name: 'benefits', label: 'Benefits', type: 'textarea', rows: 8 },
  {
    name: 'guidelines',
    label: 'Guidelines',
    type: 'textarea',
    rows: 10,
    help: 'Included in the script prompt. v1 read the wrong column range, so this never reached the model.',
  },
];

export const LOCATION_FIELDS: FieldSpec[] = [
  { name: 'name', label: 'Name', type: 'text', required: true },
  {
    name: 'tag',
    label: 'Tag',
    type: 'text',
    required: true,
    help: 'What a script writes as location_tag, e.g. kitchen_counter',
  },
  { name: 'drive_url', label: 'Reference image (Drive link)', type: 'url', required: true },
  { name: 'influencer', label: 'Influencer', type: 'relation', collection: 'influencers' },
  { name: 'is_global', label: 'Available to everyone', type: 'bool' },
  { name: 'sort', label: 'Sort order', type: 'number' },
];

interface Props {
  collection: string;
  title: string;
  description: string;
  sort: string;
  fields: FieldSpec[];
  labelField: string;
}

export default function RecordEditor({ collection, title, description, sort, fields, labelField }: Props) {
  const records = useCollection<RecordModel>(collection, { sort });
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [draft, setDraft] = useState<Record<string, unknown>>({});
  const [query, setQuery] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Relation options are loaded once for every relation field in the spec.
  const relationCollections = useMemo(
    () => [...new Set(fields.filter((f) => f.type === 'relation').map((f) => f.collection!))],
    [fields]
  );
  const [relationOptions, setRelationOptions] = useState<Record<string, RecordModel[]>>({});

  useEffect(() => {
    void Promise.all(
      relationCollections.map(async (name) => {
        const items = await pb.collection(name).getFullList({ batch: 200 });
        return [name, items] as const;
      })
    )
      .then((entries) => setRelationOptions(Object.fromEntries(entries)))
      .catch((err) => setError(errorText(err)));
  }, [relationCollections]);

  const selected = records.items.find((r) => r.id === selectedId) ?? null;

  // `creating` guards the sync effect below: startNew() sets selectedId to null
  // and a blank draft in the same batch, and without the guard the effect would
  // immediately clobber that draft with {} and close the form.
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (!selectedId && creating) return;
    if (selectedId) setCreating(false);
    setDraft(selected ? { ...selected } : {});
    setError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId, selected?.updated, creating]);

  const filtered = records.items.filter((r) =>
    query.trim()
      ? JSON.stringify(r).toLowerCase().includes(query.trim().toLowerCase())
      : true
  );

  const startNew = () => {
    setCreating(true);
    setSelectedId(null);
    const blank: Record<string, unknown> = {};
    for (const f of fields) {
      blank[f.name] = f.type === 'bool' ? true : f.type === 'number' ? 0 : f.multiple ? [] : '';
    }
    setDraft(blank);
  };

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      const payload: Record<string, unknown> = {};
      for (const f of fields) payload[f.name] = draft[f.name] ?? (f.multiple ? [] : '');

      if (selectedId) {
        await pb.collection(collection).update(selectedId, payload);
      } else {
        const created = await pb.collection(collection).create(payload);
        setCreating(false);
        setSelectedId(created.id);
      }
      await records.reload();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!selectedId) return;
    if (
      !(await confirmDialog({
        title: 'Delete this record?',
        message: 'Campaigns already using it keep their copy of the data.',
        confirmLabel: 'Delete',
        danger: true,
      }))
    ) {
      return;
    }
    setBusy(true);
    try {
      await pb.collection(collection).delete(selectedId);
      setCreating(false);
      setSelectedId(null);
      setDraft({});
      await records.reload();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  const editing = selectedId !== null || creating;

  return (
    <div className="grid lg:grid-cols-3 gap-6 items-start">
      <div className="zen-card p-4 space-y-3">
        <div className="flex items-center justify-between gap-2">
          <h3 className="font-serif text-base font-medium">{title}</h3>
          <button
            onClick={startNew}
            className="p-1.5 rounded-lg text-natural-sage hover:bg-natural-sage/10 cursor-pointer"
            title="New"
          >
            <Plus size={15} />
          </button>
        </div>

        <div className="relative">
          <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-natural-text/35" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search…"
            className="zen-input pl-8 py-2 text-xs"
          />
        </div>

        <div className="space-y-1 max-h-[560px] overflow-y-auto -mr-2 pr-2">
          {filtered.map((r) => (
            <button
              key={r.id}
              onClick={() => setSelectedId(r.id)}
              className={`w-full text-left px-3 py-2 rounded-xl text-xs transition-colors cursor-pointer ${
                r.id === selectedId
                  ? 'bg-natural-sage/10 text-natural-sage font-semibold'
                  : 'hover:bg-natural-bg'
              }`}
            >
              <span className="block truncate">{String(r[labelField] ?? r.id)}</span>
              {'active' in r && r.active === false && (
                <span className="text-[10px] text-natural-text/40">inactive</span>
              )}
            </button>
          ))}
          {filtered.length === 0 && (
            <p className="text-xs text-natural-text/40 py-6 text-center">Nothing here yet.</p>
          )}
        </div>
      </div>

      <div className="lg:col-span-2 zen-card p-6 space-y-4">
        <div>
          <h3 className="font-serif text-lg font-medium">
            {selectedId ? `Edit ${title.toLowerCase().replace(/s$/, '')}` : `New ${title.toLowerCase().replace(/s$/, '')}`}
          </h3>
          <p className="text-[11px] text-natural-text/55 mt-1 leading-relaxed">{description}</p>
        </div>

        {!editing ? (
          <p className="text-xs text-natural-text/45 py-10 text-center">
            Choose something on the left, or create a new one.
          </p>
        ) : (
          <>
            <div className="space-y-4">
              {fields.map((field) => (
                <Field
                  key={field.name}
                  spec={field}
                  value={draft[field.name]}
                  options={field.collection ? (relationOptions[field.collection] ?? []) : []}
                  onChange={(value) => setDraft((d) => ({ ...d, [field.name]: value }))}
                />
              ))}
            </div>

            {error && (
              <div className="p-3 bg-red-50 text-red-600 border border-red-200 rounded-xl text-xs flex items-start gap-2">
                <AlertCircle size={14} className="shrink-0 mt-0.5" />
                <span className="break-words">{error}</span>
              </div>
            )}

            <div className="flex gap-2 pt-1">
              <button onClick={save} disabled={busy} className="zen-btn-primary flex-1">
                <Save size={14} />
                {busy ? 'Saving…' : selectedId ? 'Save changes' : 'Create'}
              </button>
              {selectedId && (
                <button onClick={remove} disabled={busy} className="zen-btn-danger">
                  <Trash2 size={14} />
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function Field({
  spec,
  value,
  options,
  onChange,
}: {
  spec: FieldSpec;
  value: unknown;
  options: RecordModel[];
  onChange: (value: unknown) => void;
}) {
  const label = (
    <label className="zen-label">
      {spec.label}
      {spec.required && <span className="text-red-500 ml-1">*</span>}
    </label>
  );

  const help = spec.help && (
    <p className="text-[11px] text-natural-text/45 mt-1 leading-relaxed">{spec.help}</p>
  );

  if (spec.type === 'bool') {
    return (
      <div>
        <label className="flex items-center gap-2.5 cursor-pointer">
          <input
            type="checkbox"
            checked={Boolean(value)}
            onChange={(e) => onChange(e.target.checked)}
            className="w-4 h-4 accent-[var(--color-natural-sage)]"
          />
          <span className="text-xs font-semibold">{spec.label}</span>
        </label>
        {help}
      </div>
    );
  }

  if (spec.type === 'select') {
    return (
      <div>
        {label}
        <select
          value={String(value ?? '')}
          onChange={(e) => onChange(e.target.value)}
          className="zen-input"
        >
          <option value="">—</option>
          {(spec.options ?? []).map((o) => (
            <option key={o} value={o}>
              {o}
            </option>
          ))}
        </select>
        {help}
      </div>
    );
  }

  if (spec.type === 'relation') {
    const labelFor = (r: RecordModel) =>
      String(r.name ?? r.display_name ?? r.email ?? r.id);

    if (spec.multiple) {
      const selected = Array.isArray(value) ? (value as string[]) : [];
      return (
        <div>
          {label}
          <div className="border border-natural-border rounded-xl p-3 max-h-44 overflow-y-auto space-y-1.5">
            {options.map((o) => (
              <label key={o.id} className="flex items-center gap-2 cursor-pointer text-xs">
                <input
                  type="checkbox"
                  checked={selected.includes(o.id)}
                  onChange={(e) =>
                    onChange(
                      e.target.checked
                        ? [...selected, o.id]
                        : selected.filter((id) => id !== o.id)
                    )
                  }
                  className="w-3.5 h-3.5 accent-[var(--color-natural-sage)]"
                />
                {labelFor(o)}
              </label>
            ))}
            {options.length === 0 && (
              <p className="text-[11px] text-natural-text/40">Nothing to choose from yet.</p>
            )}
          </div>
          {help}
        </div>
      );
    }

    return (
      <div>
        {label}
        <select
          value={String(value ?? '')}
          onChange={(e) => onChange(e.target.value)}
          className="zen-input"
        >
          <option value="">—</option>
          {options.map((o) => (
            <option key={o.id} value={o.id}>
              {labelFor(o)}
            </option>
          ))}
        </select>
        {help}
      </div>
    );
  }

  if (spec.type === 'textarea') {
    return (
      <div>
        {label}
        <textarea
          value={String(value ?? '')}
          onChange={(e) => onChange(e.target.value)}
          rows={spec.rows ?? 4}
          className="zen-input text-xs leading-relaxed resize-y"
        />
        {help}
      </div>
    );
  }

  return (
    <div>
      {label}
      <input
        type={spec.type === 'number' ? 'number' : 'text'}
        value={String(value ?? '')}
        onChange={(e) => onChange(spec.type === 'number' ? Number(e.target.value) : e.target.value)}
        className={`zen-input ${spec.type === 'url' ? 'font-mono text-xs' : ''}`}
      />
      {help}
    </div>
  );
}
